import { Box, Typography, Grid, Card } from "@mui/material";
import { useMemo, useState, useCallback } from "react";
import { ReportValueCardProps } from "helpers/types";
import { drawdownComparisonChartStyles } from "../../../styles/DrawdownComparisonChart.styles";
import { DrawdownComparisonEditModal } from "./DrawdownComparisonEditModal";

interface DrawdownComparisonChartProps {
  values: ReportValueCardProps[];
}

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const DrawdownComparisonChart = ({
  values,
}: DrawdownComparisonChartProps) => {
  const [maxDrop, setMaxDrop] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stockDrop = useMemo(() => {
    const stockValue = values.find((v) => v.label === "Max Stock Drop");
    return typeof stockValue?.value === "number"
      ? stockValue.value
      : parseFloat(stockValue?.value.toString() || "0") || 0;
  }, [values]);

  const oeaDrop = useMemo(() => {
    const oeaValue = values.find((v) => v.label === "OEA Drop");
    return typeof oeaValue?.value === "number"
      ? oeaValue.value
      : parseFloat(oeaValue?.value.toString() || "0") || 0;
  }, [values]);

  const calculatedMaxDrop = useMemo(() => {
    return Math.max(Math.abs(stockDrop), Math.abs(oeaDrop), maxDrop);
  }, [stockDrop, oeaDrop, maxDrop]);

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleMaxDropChange = useCallback((newMaxDrop: number) => {
    setMaxDrop(newMaxDrop);
  }, []);

  const stockPercentage = useMemo(
    () => Math.min((Math.abs(stockDrop) / calculatedMaxDrop) * 100, 100),
    [stockDrop, calculatedMaxDrop],
  );

  const oeaPercentage = useMemo(
    () => Math.min((Math.abs(oeaDrop) / calculatedMaxDrop) * 100, 100),
    [oeaDrop, calculatedMaxDrop],
  );

  const stockFilledWidth = useMemo(
    () => `${stockPercentage}%`,
    [stockPercentage],
  );

  const oeaFilledWidth = useMemo(() => `${oeaPercentage}%`, [oeaPercentage]);

  return (
    <>
      <Card
        sx={drawdownComparisonChartStyles.card}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Click to edit drawdown limit"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <Box sx={drawdownComparisonChartStyles.titleContainer}>
              <Typography sx={drawdownComparisonChartStyles.titleLine1}>
                Drawdown
              </Typography>
              <Typography sx={drawdownComparisonChartStyles.titleLine2}>
                Comparison
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={drawdownComparisonChartStyles.barsContainer}>
              <Box sx={drawdownComparisonChartStyles.barRow}>
                <Typography sx={drawdownComparisonChartStyles.barLabel}>
                  Stock
                </Typography>
                <Box sx={drawdownComparisonChartStyles.barContainer}>
                  <Box
                    sx={{
                      ...drawdownComparisonChartStyles.barStockFilled,
                      width: stockFilledWidth,
                    }}
                  />
                </Box>
                <Typography sx={drawdownComparisonChartStyles.barStockValue}>
                  {formatPercentage(stockDrop)}
                </Typography>
              </Box>

              <Box
                sx={{
                  ...drawdownComparisonChartStyles.barRow,
                  marginBottom: 0,
                }}
              >
                <Typography sx={drawdownComparisonChartStyles.barLabel}>
                  OEA
                </Typography>
                <Box sx={drawdownComparisonChartStyles.barContainer}>
                  <Box
                    sx={{
                      ...drawdownComparisonChartStyles.barOeaFilled,
                      width: oeaFilledWidth,
                    }}
                  />
                </Box>
                <Typography sx={drawdownComparisonChartStyles.barOeaValue}>
                  {formatPercentage(oeaDrop)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>

      <DrawdownComparisonEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        maxDrop={maxDrop}
        onMaxDropChange={handleMaxDropChange}
      />
    </>
  );
};
